import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import url from 'url';

import DiscoverAlbumFolderStartGeneric from './start/DiscoverAlbumFolderStartGeneric';
import { ALBUM_SUBTYPE, ALBUM_TYPE } from '../../constants/albums';
import DiscoverAlbumFolderStartCamps from './start/DiscoverAlbumFolderStartCamps';
import Spinner from '../global/Spinner';
import DiscoverAlbumFolderStartSchools from './start/DiscoverAlbumFolderStartSchools';
import DiscoverAlbumFolderStartWeddings from './start/DiscoverAlbumFolderStartWeddings';
import { sentryCaptureMessage } from '../../util/sentry';
import OnboardingLandingSchoolsHeadMeta from '../onboarding/start/OnboardingLandingSchoolsHeadMeta';
import OnboardingLandingCampsHeadMeta from '../onboarding/start/OnboardingLandingCampsHeadMeta';
import AlbumFolderStartShutterbug from '../../apps/temp/views/onboarding/AlbumFolderStart/components/AlbumFolderStartShutterbug';
import DiscoverAlbumFolderStartSports from './start/DiscoverAlbumFolderStartSports';
import OnboardingLandingSportsHeadMeta from '../onboarding/start/OnboardingLandingSportsHeadMeta';
import DiscoverAlbumFolderStartChurch from './start/DiscoverAlbumFolderStartChurch';
import OnboardingLandingChurchHeadMeta from '../onboarding/start/OnboardingLandingChurchHeadMeta';
import { isWeddingsContent } from '../../util/album';
import { windowLocationAssign } from '../../util/navigation';

class DiscoverAlbumFolderStart extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      album_type: PropTypes.string,
      album_subtype: PropTypes.string,
      is_demo: PropTypes.bool,
      has_charity: PropTypes.bool,
      charity_name: PropTypes.string,
      charity_percentage: PropTypes.number,
      account: PropTypes.shape({
        name: PropTypes.string,
        logo_image: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      custom_onboarding_url: PropTypes.string,
    }),
    authenticated: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
    loading: PropTypes.bool,
  };

  // If this page is accessed with a `joinType` query it means
  // we're coming in from Unbounce so we need to skip this page entirely.
  // TODO: Temporary measure
  componentDidMount() {
    const { albumFolder, location } = this.props;

    if (!location?.search && !albumFolder) {
      return;
    }

    const { joinType } = qs.parse(location?.search ?? '');

    if (joinType) {
      this.onGetStarted();
    }

    // As of 14/10/25, we want to keep this page for SEL
    if (
      [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT].includes(
        albumFolder?.album_type,
      ) ||
      [ALBUM_SUBTYPE.WEDDINGS].includes(albumFolder?.album_subtype)
    ) {
      this.onGetStarted();
    }
  }

  componentDidUpdate() {
    const { loading, albumFolder } = this.props;

    // If there is a custom url set on the album folder, redirect to it.
    if (albumFolder?.custom_onboarding_url) {
      // Add the folder id so that unbounce knows how to get back.
      // eg unbounce could then make a '/discover/album-folder/{album-folder-id}/faces' link.
      windowLocationAssign(
        `${albumFolder.custom_onboarding_url}?album_folder=${albumFolder.id}`,
      );
    }

    if (!loading && !albumFolder) {
      sentryCaptureMessage('[DiscoverAlbumFolderStart] - No album folder', {
        level: 'warning',
        extra: {
          ...this.props,
        },
      });
    }

    if (!albumFolder) {
      return;
    }

    // As per WAL-6143, we want to skip this page.
    // As of 14/10/25, we want to keep this page for SEL
    if (
      [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT].includes(
        albumFolder?.album_type,
      ) ||
      [ALBUM_SUBTYPE.WEDDINGS].includes(albumFolder?.album_subtype)
    ) {
      this.onGetStarted();
    }
  }

  onGetStarted = () => {
    const { albumFolder, authenticated, history, location } = this.props;
    const currentSearch = location?.search ?? '';
    const currentPath = location?.pathname ?? '';

    // Get parsed query parameters
    const queryParams = qs.parse(currentSearch);

    // Check if the URL is from a QR code scan
    const isFromQrCode =
      /^\/discover\/album-folder\/[^/]+\/start$/.test(currentPath) &&
      (currentSearch.includes('utm_medium=qr-code') ||
        queryParams.next?.includes('utm_medium=qr-code')) &&
      !queryParams.fromGallery; // Explicit check for fromGallery parameter

    const shouldPreserveQueryParams = !isFromQrCode;

    // Build target URL, preserve parameters when needed
    const targetUrl = url.format({
      pathname: `/discover/album-folder/${albumFolder.id}`,
      search: shouldPreserveQueryParams ? currentSearch : '',
    });

    const joinsEnabled =
      albumFolder?.joins_enabled || albumFolder?.root?.joins_enabled;
    const isWebGallery =
      albumFolder?.is_web_gallery || albumFolder?.root?.is_web_gallery;

    if (
      isWebGallery &&
      joinsEnabled &&
      ![ALBUM_SUBTYPE.SCHOOLS, ALBUM_SUBTYPE.CAMPS].includes(
        albumFolder?.album_subtype,
      )
    ) {
      const isIndividualAlbum = albumFolder?.individual_albums;
      const firstAlbumId = albumFolder?.children?.albums?.records?.[0]?.id;
      if (isFromQrCode) {
        if (isIndividualAlbum && firstAlbumId) {
          history.replace({
            pathname: `/galleries/view/album/${firstAlbumId}`,
            search: '',
          });
          return;
        }
        history.replace({
          pathname: `/galleries/view/folder/${albumFolder.id}`,
          search: '',
        });
        return;
      }
    }

    if (authenticated) {
      history.replace(targetUrl);
    } else {
      history.replace({
        pathname: '/auth/onboarding',
        search: `?${qs.stringify({
          albumFolderId: albumFolder.id,
          next: targetUrl,
        })}`,
      });
    }
  };

  render() {
    const { albumFolder, loading } = this.props;

    if (loading || !albumFolder) {
      return (
        <div className="text-center mt-5">
          <Spinner />
        </div>
      );
    }

    if (isWeddingsContent(albumFolder)) {
      return (
        <DiscoverAlbumFolderStartWeddings
          albumFolder={albumFolder}
          onGetStarted={this.onGetStarted}
        />
      );
    }

    if (
      albumFolder.album_type === ALBUM_TYPE.SHUTTERBUG ||
      albumFolder.album_subtype === ALBUM_SUBTYPE.GREEK ||
      albumFolder.album_type === ALBUM_TYPE.EVENT
    ) {
      return (
        <AlbumFolderStartShutterbug
          albumFolder={albumFolder}
          onGetStarted={this.onGetStarted}
        />
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.CAMPS) {
      return (
        <Fragment>
          <OnboardingLandingCampsHeadMeta />
          <DiscoverAlbumFolderStartCamps
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.SCHOOLS) {
      return (
        <Fragment>
          <OnboardingLandingSchoolsHeadMeta />
          <DiscoverAlbumFolderStartSchools
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.SPORTS) {
      return (
        <Fragment>
          <OnboardingLandingSportsHeadMeta />
          <DiscoverAlbumFolderStartSports
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.CHURCH) {
      return (
        <Fragment>
          <OnboardingLandingChurchHeadMeta />
          <DiscoverAlbumFolderStartChurch
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    return (
      <DiscoverAlbumFolderStartGeneric
        albumFolder={albumFolder}
        onGetStarted={this.onGetStarted}
      />
    );
  }
}

export default DiscoverAlbumFolderStart;
